import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { IngredientTraceCode } from '../../types/ingredientTraceCode';
import { UsageRecord } from '../../types/usageRecord';
import { ingredientTraceCodeApiService } from '../../services/ingredientTraceCodeApiService';
import type { RootState } from '../store';

// State structure with loading and error
interface IngredientTraceCodesState {
  traceCodes: IngredientTraceCode[];
  loading: boolean;
  error: string | null;
  usageData: Record<string, UsageRecord[]>;
  usageLoading: Record<string, boolean>;
  usageFetched: Record<string, boolean>;
  lotNumberMap: Record<string, string[]>;
}

// Initial state
const initialState: IngredientTraceCodesState = {
  traceCodes: [],
  loading: false,
  error: null,
  usageData: {},
  usageLoading: {},
  usageFetched: {},
  lotNumberMap: {}
};

// Helper function to create a blank ingredient trace code
export function createBlankIngredientTraceCode(): IngredientTraceCode {
  const today = new Date().toISOString().split('T')[0];
  return {
    id: uuidv4(),
    supplierId: '',
    ingredientName: '',
    supplierLotNumber: '',
    internalTraceCode: '',
    dateUsed: '',
    quantityUsed: undefined,
    finishedProduct: '',
    verifiedBy: '',
    comments: '',
    createdAt: new Date().toISOString(),
    ingredientReceivingId: undefined,
    dateReceived: today
  };
}

// Async thunks

/**
 * Fetch all ingredient trace codes from the API
 */
export const fetchIngredientTraceCodes = createAsyncThunk(
  'ingredientTraceCodes/fetchIngredientTraceCodes',
  async () => {
    return await ingredientTraceCodeApiService.fetchIngredientTraceCodes();
  }
);

/**
 * Fetch a single ingredient trace code by ID
 */
export const fetchIngredientTraceCodeById = createAsyncThunk(
  'ingredientTraceCodes/fetchIngredientTraceCodeById',
  async (id: string) => {
    return await ingredientTraceCodeApiService.fetchIngredientTraceCodeById(id);
  }
);

/**
 * Create a new ingredient trace code
 */
export const createIngredientTraceCode = createAsyncThunk(
  'ingredientTraceCodes/createIngredientTraceCode',
  async (traceCode: IngredientTraceCode) => {
    return await ingredientTraceCodeApiService.createIngredientTraceCode(traceCode);
  }
);

/**
 * Update an existing ingredient trace code
 */
export const updateIngredientTraceCode = createAsyncThunk(
  'ingredientTraceCodes/updateIngredientTraceCode',
  async (traceCode: IngredientTraceCode) => {
    return await ingredientTraceCodeApiService.updateIngredientTraceCode(traceCode);
  }
);

/**
 * Delete an ingredient trace code by ID
 */
export const deleteIngredientTraceCode = createAsyncThunk(
  'ingredientTraceCodes/deleteIngredientTraceCode',
  async (id: string) => {
    await ingredientTraceCodeApiService.deleteIngredientTraceCode(id);
    return id; // Return id for reducer to remove from state
  }
);

/**
 * Fetch usage records for a single ingredient trace code
 * Only fetches if not already fetched or loading (checked via condition)
 */
export const fetchIngredientTraceCodeUsage = createAsyncThunk(
  'ingredientTraceCodes/fetchUsage',
  async (id: string, { rejectWithValue }) => {
    try {
      console.log('[ITCUsage] Fetching usage for trace code:', id);
      const records = await ingredientTraceCodeApiService.fetchIngredientTraceCodeUsage(id);
      console.log('[ITCUsage] Successfully fetched', records.length, 'usage records for:', id);
      return { id, records };
    } catch (error: any) {
      console.error('[ITCUsage] Error fetching usage for:', id, error);
      return rejectWithValue({ id, error: error.message });
    }
  },
  {
    // Prevent the thunk from running if already fetched or loading
    condition: (id, { getState }) => {
      const state = getState() as RootState;
      const { usageFetched, usageLoading } = state.ingredientTraceCodes;

      console.log('[ITCUsage] Condition check for ID:', id, {
        usageFetched: usageFetched[id],
        usageLoading: usageLoading[id]
      });

      // Return false to prevent the thunk from running
      if (usageFetched[id] || usageLoading[id]) {
        console.log('[ITCUsage] Skipping fetch - already fetched or loading');
        return false;
      }

      return true;
    }
  }
);

/**
 * Fetch bulk mapping of trace code IDs to batch lot numbers
 */
export const fetchLotNumberMap = createAsyncThunk(
  'ingredientTraceCodes/fetchLotNumberMap',
  async () => {
    return await ingredientTraceCodeApiService.fetchLotNumberMap();
  }
);

const ingredientTraceCodesSlice = createSlice({
  name: 'ingredientTraceCodes',
  initialState,
  reducers: {
    /**
     * Add a trace code directly to the state
     * Used when a trace code is created automatically (e.g., from receiving log)
     */
    addTraceCode: (state, action) => {
      console.log('[DEBUG addTraceCode] Reducer called with payload:', action.payload);
      console.log('[DEBUG addTraceCode] Current traceCodes count:', state.traceCodes.length);
      if (action.payload) {
        state.traceCodes.push(action.payload);
        console.log('[DEBUG addTraceCode] ✓ Trace code added! New count:', state.traceCodes.length);
      } else {
        console.log('[DEBUG addTraceCode] ⚠️ No payload to add!');
      }
    },
    /**
     * Remove trace codes by their IDs
     * Used when receiving log entries are deleted (CASCADE delete)
     */
    removeTraceCodesByIds: (state, action) => {
      const idsToRemove = action.payload;
      console.log('[DEBUG removeTraceCodesByIds] Removing trace codes with IDs:', idsToRemove);
      console.log('[DEBUG removeTraceCodesByIds] Current traceCodes count:', state.traceCodes.length);
      if (idsToRemove && idsToRemove.length > 0) {
        state.traceCodes = state.traceCodes.filter(tc => !idsToRemove.includes(tc.id));
        console.log('[DEBUG removeTraceCodesByIds] ✓ Trace codes removed! New count:', state.traceCodes.length);
      } else {
        console.log('[DEBUG removeTraceCodesByIds] ⚠️ No IDs to remove!');
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch all ingredient trace codes
    builder
      .addCase(fetchIngredientTraceCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIngredientTraceCodes.fulfilled, (state, action) => {
        state.loading = false;
        state.traceCodes = action.payload;
      })
      .addCase(fetchIngredientTraceCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch ingredient trace codes';
      });

    // Fetch ingredient trace code by ID
    builder
      .addCase(fetchIngredientTraceCodeById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIngredientTraceCodeById.fulfilled, (state, action) => {
        state.loading = false;
        // Update the trace code in the array if it exists, otherwise add it
        const index = state.traceCodes.findIndex(tc => tc.id === action.payload.id);
        if (index !== -1) {
          state.traceCodes[index] = action.payload;
        } else {
          state.traceCodes.push(action.payload);
        }
      })
      .addCase(fetchIngredientTraceCodeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch ingredient trace code';
      });

    // Create ingredient trace code
    builder
      .addCase(createIngredientTraceCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createIngredientTraceCode.fulfilled, (state, action) => {
        state.loading = false;
        state.traceCodes.push(action.payload);
      })
      .addCase(createIngredientTraceCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create ingredient trace code';
      });

    // Update ingredient trace code
    builder
      .addCase(updateIngredientTraceCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateIngredientTraceCode.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.traceCodes.findIndex(tc => tc.id === action.payload.id);
        if (index !== -1) {
          state.traceCodes[index] = action.payload;
        }
      })
      .addCase(updateIngredientTraceCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update ingredient trace code';
      });

    // Delete ingredient trace code
    builder
      .addCase(deleteIngredientTraceCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteIngredientTraceCode.fulfilled, (state, action) => {
        state.loading = false;
        state.traceCodes = state.traceCodes.filter(tc => tc.id !== action.payload);
      })
      .addCase(deleteIngredientTraceCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete ingredient trace code';
      });

    // Fetch usage records for trace code
    builder
      .addCase(fetchIngredientTraceCodeUsage.pending, (state, action) => {
        const id = action.meta.arg;
        state.usageLoading[id] = true;
      })
      .addCase(fetchIngredientTraceCodeUsage.fulfilled, (state, action) => {
        const { id, records } = action.payload;
        delete state.usageLoading[id];
        state.usageFetched[id] = true;
        state.usageData[id] = records;
      })
      .addCase(fetchIngredientTraceCodeUsage.rejected, (state, action) => {
        const id = action.meta.arg;
        delete state.usageLoading[id];
        // Don't add to usageFetched on error - allow retry
      });

    // Fetch lot number map
    builder
      .addCase(fetchLotNumberMap.fulfilled, (state, action) => {
        state.lotNumberMap = action.payload;
      });
  }
});

export const { addTraceCode, removeTraceCodesByIds } = ingredientTraceCodesSlice.actions;
export default ingredientTraceCodesSlice.reducer;
